import { useEffect } from 'react';
import axios from 'axios';
import { captureException, captureMessage } from '@sentry/nextjs';
import { useAtom } from 'jotai';
import { productIdAtom, productVariantsAtom } from 'global/store';
import { MAGENTO_URI } from 'global/constants';
import { Box } from '@xstyled/styled-components';
import { FlexBox } from '@/UI/Primitives/FlexBox';
import { useTranslation } from 'next-i18next';

export default function StockStatus({ id, sku, stockStatus, setStockStatus, product }) {
  const [productID, setProductID] = useAtom(productIdAtom);
  const [selectedVariants] = useAtom(productVariantsAtom);
  const { t } = useTranslation();

  // Set the product ID
  useEffect(() => setProductID(id), [id]);

  // Helper function to get current variant info
  const getCurrentVariant = () => {
    if (!selectedVariants?.length || !product?.variants) {
      return { sku: sku, isVariant: false };
    }

    const configurableOptions = product?.configurable_options;
    let variant;

    if (configurableOptions?.length > 1 && selectedVariants.length > 1) {
      // Multiple configurable options - find exact match
      const filterValue = selectedVariants.map(obj => obj.uid);
      const options = Array.from(product.variants);
      const possibleOptions = options.filter(obj =>
        filterValue.every(uuid =>
          obj.attributes.some(valueObj => valueObj.uid === uuid)
        )
      );
      if (possibleOptions.length > 0) {
        variant = possibleOptions[0]?.product;
      }
    } else if (selectedVariants.length > 0) {
      // Single configurable option or partial selection
      variant = product.variants.find(
        ({ product: variantProduct }) => variantProduct?.sku === selectedVariants[0]?.sku
      )?.product;
      
      if (!variant) {
        variant = product.variants.find(({ attributes }) => {
          return attributes?.find(
            item => item?.uid === selectedVariants[0]?.uid
          );
        })?.product;
      }
    }

    return {
      sku: variant?.sku || sku,
      isVariant: !!variant,
      variantId: variant?.id
    };
  };

  // Get stock statuses of warehouses
  useEffect(() => {
    if (!productID) return;

    const currentVariant = getCurrentVariant();

    const targetProductId = currentVariant.isVariant ? currentVariant.variantId : productID;

    axios
      .post(`${MAGENTO_URI}/warehouse.php`, { 
        product_id: targetProductId,
        sku: currentVariant.sku
      })
      .then(res => {
        const { success, result } = res.data;
        let kvartalStock = 0;
        let warehouseStock = 0;
        
        if (success && result) {
          kvartalStock = Number(
            result?.find(({ warehouse_id: id }) => id === '2')?.qty || 0
          );
          warehouseStock = Number(
            result?.find(({ warehouse_id: id }) => id === '1')?.qty || 0
          );
        }
        
        
        setStockStatus({
          kvartal: { qty: kvartalStock },
          warehouse: { qty: warehouseStock }
        });
      })
      .catch(error => {
        captureException(error);
      });
  }, [productID, selectedVariants, product?.variants, sku]);

  return (
    <FlexBox spaceX={24}>
      {stockStatus?.warehouse?.qty > 0 && <Warehouse name={t('stock')} />}
    </FlexBox>
  );
}

const Warehouse = ({ name }) => {
  return (
    <FlexBox as="p" align="center" mt={-16} color="#52C1D3">
      {name}
      <Box as="span" w={12} h={12} ml={6} bg="#96E799" borderRadius="50%" />
    </FlexBox>
  );
};